import { env } from './env';

/**
 * API 호출 중 발생한 오류를 정규화한 클래스.
 *
 * 실제 fetch 실패(네트워크), 비-2xx 응답, JSON 파싱 실패 등
 * 모든 케이스를 동일한 형태로 throw해 상위에서 일관되게 처리할 수 있도록 한다.
 */
export class ApiError extends Error {
  /** HTTP 상태 코드. 네트워크 오류 시 0. */
  public readonly status: number;
  /** 서버가 반환한 에러 코드(예: `INVALID_CODE`, `ALREADY_USED`). 없으면 `undefined`. */
  public readonly code: string | undefined;
  /** 서버가 반환한 raw 응답 바디(있을 경우). 디버깅용. */
  public readonly payload: unknown;

  constructor(message: string, status: number, code: string | undefined, payload: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

/**
 * 서버가 보낼 수 있는 에러 응답 바디 형태들의 합집합.
 *
 * - **DARAK-KIDDAY API의 실제 형태** (NestJS + 커스텀 ExceptionFilter, 1순위):
 *   `{ statusCode: 401, timestamp, path, code: "LOGIN_FAIL", description: "계정을 찾을 수 없습니다..." }`
 *   `{ statusCode: 400, timestamp, path, code: "Bad Request", description: "code must be a string" }`
 *   `{ statusCode: 404, timestamp, path, code: "USER_NOT_FOUND", description: "유저를 찾을 수 없습니다." }`
 * - **NestJS 표준** (HttpException + 글로벌 ValidationPipe, 호환성용):
 *   `{ statusCode: 400, message: "이미 등록된 코드", error: "Bad Request" }`
 *   `{ statusCode: 400, message: ["code must match pattern ..."], error: "Bad Request" }`
 * - **자체 포맷** (`error`가 객체인 케이스 — 일부 레거시/커스텀 인터셉터):
 *   `{ error: { code: "INVALID_CODE", message: "..." } }`
 *
 * 실제 응답에 어떤 키가 있는지는 백엔드 구성에 따라 달라지므로 모두 옵셔널이며,
 * `extractErrorMessage`가 아래 우선순위로 메시지를 결정한다.
 */
interface ApiErrorPayload {
  /** NestJS HttpException의 `statusCode`. 보조 정보. */
  statusCode?: number;
  /**
   * DARAK-KIDDAY API의 실제 사용자 메시지 키.
   * 단일 문자열이 일반적이지만, 다중 검증 실패 케이스를 대비해 배열도 허용한다.
   */
  description?: string | string[];
  /** DARAK-KIDDAY API의 에러 코드 키 (예: `LOGIN_FAIL`, `USER_NOT_FOUND`, `Bad Request`). */
  code?: string;
  /** NestJS HttpException의 발생 시각 (디버깅용 보조 정보). */
  timestamp?: string;
  /** NestJS HttpException이 발생한 요청 경로 (디버깅용 보조 정보). */
  path?: string;
  /**
   * NestJS 표준 메시지 키 — 단일 문자열 또는 class-validator 검증 실패 배열.
   * DARAK-KIDDAY API는 사용하지 않지만 호환을 위해 남겨둔다.
   */
  message?: string | string[];
  /**
   * NestJS 표준 `error` 키 — 보통 `'Bad Request'` 같은 enum 문자열.
   * 자체 포맷에서는 `{ code, message }` 객체로 오는 경우도 있다.
   */
  error?: string | { code?: string; message?: string };
}

/**
 * fetch 옵션 — `body`는 JSON 직렬화 전 객체를 그대로 받는다.
 */
export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON 직렬화 전 본문 객체. */
  body?: unknown;
  /** 절대 URL을 직접 넘기고 싶을 때 사용. (보통은 path만 넘긴다.) */
  absoluteUrl?: boolean;
}

/** 비-JSON 본문에서 메시지로 사용할 최대 문자 수 (텍스트 응답용). */
const MAX_TEXT_BODY_LENGTH = 200;

/**
 * 안전한 JSON 파싱 — 실패 시 `null` 반환.
 * 응답이 JSON Content-Type을 가지지만 실제 본문이 비어 있거나 잘못된 경우를 대비.
 */
function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * `string[]` 메시지를 사용자에게 보여줄 한 덩어리 텍스트로 가공한다.
 *
 * - 비어 있거나 공백뿐인 항목은 제거.
 * - 1개면 그대로, 2개 이상이면 `• 항목` + 줄바꿈으로 결합한다 (정책 P1=B).
 * - 토스트의 `.message`에 `white-space: pre-line`이 걸려 있어 줄바꿈이 시각적으로 보존된다.
 */
function formatMessageList(messages: readonly unknown[]): string {
  const trimmed = messages
    .filter((m): m is string => typeof m === 'string')
    .map((m) => m.trim())
    .filter((m) => m.length > 0);
  if (trimmed.length === 0) {
    return '';
  }
  // length 보장은 했지만 TS의 `noUncheckedIndexedAccess` 옵션 때문에 명시적으로 폴백을 둔다.
  const first = trimmed[0] ?? '';
  if (trimmed.length === 1) {
    return first;
  }
  return trimmed.map((m) => `• ${m}`).join('\n');
}

/**
 * 비-2xx 응답에서 사용자에게 보여줄 에러 메시지를 결정한다.
 *
 * 우선순위 (DARAK-KIDDAY API → NestJS 표준 → 자체 포맷 → 텍스트 폴백):
 *   1. `payload.description` (string)  — DARAK-KIDDAY API의 사용자 메시지 키
 *   2. `payload.description` (string[]) — 다중 검증 실패 대비
 *   3. `payload.error.message` — 자체 포맷의 객체형 에러
 *   4. `payload.message` (string)  — NestJS 표준 단일 메시지
 *   5. `payload.message` (string[]) — NestJS class-validator 다중 메시지
 *   6. `payload.error` (string) — NestJS의 `'Bad Request'` 같은 enum (마지막 수단)
 *   7. 비-JSON 본문(HTML 제외) 일부 — text/plain 등
 *   8. `요청이 실패했어요 (HTTP {status} {statusText})`
 *
 * HTML 응답(게이트웨이 502 페이지 등)은 가독성·보안 이슈가 있어 본문을 노출하지 않고
 * 8번 폴백으로 떨어뜨린다 (정책 P3=B). 5xx 응답에서도 서버 메시지가 있으면 그대로
 * 노출하는데, NestJS는 기본적으로 스택을 응답 본문에 포함하지 않으므로 정보 유출
 * 위험이 낮고 진단 편의가 더 큰 것으로 본다 (정책 P4=A).
 *
 * @param payload - JSON 파싱된 응답 본문 (실패 시 `null`).
 * @param contentType - 응답의 `Content-Type` 헤더 값.
 * @param rawText - 응답의 원본 문자열 본문 (JSON이든 아니든 항상 채워짐).
 * @param response - HTTP 상태/statusText 폴백용.
 */
export function extractErrorMessage(
  payload: unknown,
  contentType: string,
  rawText: string,
  response: Pick<Response, 'status' | 'statusText'>,
): string {
  if (payload !== null && typeof payload === 'object') {
    const p = payload as ApiErrorPayload;

    // 1. DARAK-KIDDAY API 단일 description
    if (typeof p.description === 'string' && p.description.trim().length > 0) {
      return p.description.trim();
    }

    // 2. DARAK-KIDDAY API 다중 description (예방적 처리)
    if (Array.isArray(p.description)) {
      const formatted = formatMessageList(p.description);
      if (formatted.length > 0) {
        return formatted;
      }
    }

    // 3. 자체 포맷: { error: { message } }
    if (
      p.error !== null &&
      typeof p.error === 'object' &&
      typeof p.error.message === 'string' &&
      p.error.message.trim().length > 0
    ) {
      return p.error.message.trim();
    }

    // 4. NestJS 표준 단일 메시지
    if (typeof p.message === 'string' && p.message.trim().length > 0) {
      return p.message.trim();
    }

    // 5. NestJS class-validator 다중 메시지
    if (Array.isArray(p.message)) {
      const formatted = formatMessageList(p.message);
      if (formatted.length > 0) {
        return formatted;
      }
    }

    // 6. NestJS error enum 문자열 — 마지막 수단 (영문이라 사용자 친화도 ↓)
    if (typeof p.error === 'string' && p.error.trim().length > 0) {
      return p.error.trim();
    }
  }

  // 5. 비-JSON 본문 (HTML 제외): text/plain 등은 앞 200자만 노출
  const isHtml = contentType.toLowerCase().includes('text/html');
  if (!isHtml && rawText.trim().length > 0) {
    return rawText.trim().slice(0, MAX_TEXT_BODY_LENGTH);
  }

  // 6. 최종 폴백 — statusText까지 포함해서 진단성 향상
  const statusText = response.statusText.length > 0 ? ` ${response.statusText}` : '';
  return `요청이 실패했어요 (HTTP ${String(response.status)}${statusText})`;
}

/**
 * 서버가 반환한 에러 코드를 추출한다 (없으면 `undefined`).
 *
 * 우선순위:
 *   1. `payload.code` — DARAK-KIDDAY API 표준 코드 키 (`USER_NOT_FOUND`, `LOGIN_FAIL`,
 *      또는 검증 실패 시 `'Bad Request'` enum 라벨이 그대로 들어오는 케이스 포함)
 *   2. `payload.error.code` — 자체 포맷의 객체형 에러 코드
 *
 * NestJS 표준의 `payload.error`(string)는 enum 라벨이라 코드로 쓰기에 부적합하므로
 * 무시한다 (DARAK-KIDDAY는 그 값을 이미 `code`로 옮겨 보낸다).
 */
function extractErrorCode(payload: unknown): string | undefined {
  if (payload === null || typeof payload !== 'object') {
    return undefined;
  }
  const p = payload as ApiErrorPayload;
  if (typeof p.code === 'string' && p.code.length > 0) {
    return p.code;
  }
  if (
    p.error !== null &&
    typeof p.error === 'object' &&
    typeof p.error.code === 'string' &&
    p.error.code.length > 0
  ) {
    return p.error.code;
  }
  return undefined;
}

/**
 * 타입 안전한 fetch 래퍼.
 *
 * @param path    `/api/codes/verify` 같은 상대 경로(또는 절대 URL).
 * @param options method, headers, body 등.
 * @returns 응답 본문(JSON 파싱된 결과).
 * @throws ApiError 비-2xx 응답 또는 네트워크 실패 시. `ApiError.message`는
 *                 가능한 한 서버가 보낸 메시지를 그대로 담는다 (단일/배열/비-JSON 모두).
 */
export async function apiFetch<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const { body, absoluteUrl = false, headers, ...rest } = options;
  const url = absoluteUrl ? path : `${env.VITE_API_BASE_URL.replace(/\/$/, '')}${path}`;

  let response: Response;
  try {
    response = await fetch(url, {
      ...rest,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    throw new ApiError(
      '네트워크 연결에 실패했어요. 잠시 후 다시 시도해 주세요.',
      0,
      'NETWORK_ERROR',
      cause,
    );
  }

  // 204 No Content는 빈 응답 — JSON 파싱하지 않는다.
  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.toLowerCase().includes('application/json');

  // Body는 한 번만 소비 가능하므로, 일단 텍스트로 한 번 읽고 필요하면 JSON으로 파싱한다.
  // 비-JSON 응답(text/plain, text/html, 빈 본문 등)에서도 원본 텍스트를 간직해
  // `extractErrorMessage`가 폴백 메시지로 활용할 수 있게 한다.
  const rawText = await response.text().catch(() => '');
  const payload: unknown = isJson && rawText.length > 0 ? safeJsonParse(rawText) : null;

  if (!response.ok) {
    const message = extractErrorMessage(payload, contentType, rawText, response);
    const code = extractErrorCode(payload);
    // payload가 null이면 디버깅을 위해 rawText 자체를 보관한다.
    throw new ApiError(message, response.status, code, payload ?? rawText);
  }

  return payload as TResponse;
}
